import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import type { StylesConfig } from "react-select";
import { formSelectStyles } from "../../pages/CreateProperty/components/DescriptionForm/twStyles";
import {
  getRankLabel,
  getRankShortLabel,
  getRankTooltip,
} from "../StarRanking/constants";
import { FLOW_TEXT_INPUT_BASE, FLOW_TEXTAREA_BASE } from "../../styles/twDesignPrimitives";

const FIELD_FONT_FAMILY = "inherit";
const FIELD_FONT_SIZE = 13;
const FIELD_FONT_WEIGHT = 500;
const FIELD_TEXT_COLOR = "#1e293b";

export const DESCRIPTION_INPUT_CLASS = `${FLOW_TEXT_INPUT_BASE} h-9 py-1.5 !font-[inherit] !text-[13px] !font-medium !leading-snug !text-slate-800`;
export const DESCRIPTION_TEXTAREA_CLASS = `${FLOW_TEXTAREA_BASE} min-h-[88px] !font-[inherit] !text-[13px] !font-medium !text-slate-800`;
export const DESCRIPTION_LABEL_CLASS =
  "mb-1 block text-xs font-medium text-slate-600";

const SELECT_CONTROL_HEIGHT = 36;

const fieldFont = {
  fontFamily: FIELD_FONT_FAMILY,
  fontSize: FIELD_FONT_SIZE,
  fontWeight: FIELD_FONT_WEIGHT,
  color: FIELD_TEXT_COLOR,
};

export const descriptionSelectStyles: StylesConfig<any, false> = {
  ...formSelectStyles,
  container: (base) => ({ ...base, width: "100%", marginBottom: 0 }),
  control: (base, props) => ({
    ...(formSelectStyles.control?.(base, props) ?? base),
    minHeight: SELECT_CONTROL_HEIGHT,
    height: SELECT_CONTROL_HEIGHT,
    width: "100%",
    ...fieldFont,
  }),
  valueContainer: (base, props) => ({
    ...(formSelectStyles.valueContainer?.(base, props) ?? base),
    paddingTop: 0,
    paddingBottom: 0,
  }),
  indicatorsContainer: (base) => ({ ...base, height: SELECT_CONTROL_HEIGHT }),
  input: (base, props) => ({
    ...(formSelectStyles.input?.(base, props) ?? base),
    ...fieldFont,
  }),
  singleValue: (base, props) => ({
    ...(formSelectStyles.singleValue?.(base, props) ?? base),
    ...fieldFont,
  }),
  placeholder: (base, props) => ({
    ...(formSelectStyles.placeholder?.(base, props) ?? base),
    fontFamily: FIELD_FONT_FAMILY,
    fontSize: FIELD_FONT_SIZE,
    fontWeight: FIELD_FONT_WEIGHT,
  }),
  option: (base, props) => ({
    ...(formSelectStyles.option?.(base, props) ?? base),
    fontFamily: FIELD_FONT_FAMILY,
    fontSize: FIELD_FONT_SIZE,
    fontWeight: FIELD_FONT_WEIGHT,
  }),
  menu: (base, props) => ({
    ...(formSelectStyles.menu?.(base, props) ?? base),
    width: "100%",
  }),
};

export function FormSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="min-w-0">
      <header className="mb-3">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {description ? (
          <p className="mt-0.5 text-xs text-slate-500">{description}</p>
        ) : null}
      </header>
      {children}
    </section>
  );
}

export function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <span className="mt-1 block text-[11px] font-medium text-red-600">
      {message}
    </span>
  );
}

export function FieldLabel({
  children,
  htmlFor,
  error,
}: {
  children: ReactNode;
  htmlFor?: string;
  error?: string;
}) {
  return (
    <label htmlFor={htmlFor} className={DESCRIPTION_LABEL_CLASS}>
      {children}
      <FieldError message={error} />
    </label>
  );
}

type ChoiceOption<T extends string | number> = {
  value: T;
  label: string;
  icon?: LucideIcon;
};

export function CompactChoiceRow<T extends string | number>({
  options,
  value,
  onChange,
}: {
  options: ChoiceOption<T>[];
  value: T | null | undefined;
  onChange: (value: T) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
      {options.map((option) => {
        const selected = option.value === value;
        const Icon = option.icon;
        return (
          <button
            key={String(option.value)}
            type="button"
            onClick={() => onChange(option.value)}
            className={`flex h-9 min-w-0 items-center justify-center gap-1.5 rounded-md border px-2 text-xs font-medium transition ${
              selected
                ? "border-slate-800 bg-slate-800 text-white"
                : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
            }`}
          >
            {Icon ? (
              <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
            ) : null}
            <span className="truncate">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function SegmentedChoice<T extends string | number>({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: T; label: string }>;
  value: T | null | undefined;
  onChange: (value: T) => void;
}) {
  return (
    <div className="flex w-full rounded-md border border-slate-200 bg-slate-50 p-0.5">
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={String(option.value)}
            type="button"
            onClick={() => onChange(option.value)}
            className={`h-8 min-w-0 flex-1 truncate rounded px-2 text-xs font-medium transition ${
              selected
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

type ZapPublicationOption = {
  value: string;
  shortLabel: string;
  label: string;
};

const ZAP_PUBLICATION_OPTIONS: ZapPublicationOption[] = [
  { value: "STANDARD", shortLabel: "Sem", label: "Sem destaque" },
  { value: "PREMIUM", shortLabel: "D", label: "Destaque" },
  { value: "TRIPLE", shortLabel: "DT", label: "Destaque Triplo" },
  { value: "SUPER_PREMIUM", shortLabel: "SD", label: "Super destaque" },
  { value: "PREMIERE_1", shortLabel: "DE", label: "Destaque Exclusivo" },
];

export function ZapPublicationChoice({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <div
        className="flex w-full rounded-md border border-slate-200 bg-slate-50 p-0.5"
        role="radiogroup"
        aria-label="Publicação no ZAP"
      >
        {ZAP_PUBLICATION_OPTIONS.map((option) => {
          const selected = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              title={option.label}
              onClick={() => onChange(option.value)}
              className={`h-9 min-w-0 flex-1 rounded px-1 text-xs font-semibold transition sm:px-2 ${
                selected
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              {option.shortLabel}
            </button>
          );
        })}
      </div>
      {ZAP_PUBLICATION_OPTIONS.find((option) => option.value === value) ? (
        <p className="mt-1 text-[11px] text-slate-500">
          {ZAP_PUBLICATION_OPTIONS.find((option) => option.value === value)?.label}
        </p>
      ) : null}
    </div>
  );
}

export function CompactRankSelect({
  value,
  onChange,
  error,
  disabled = false,
}: {
  value?: number;
  onChange: (ranking: number) => void;
  error?: string;
  disabled?: boolean;
}) {
  const ranks = [1, 2, 3, 4, 5] as const;

  return (
    <div>
      <div
        className="flex w-full rounded-md border border-slate-200 bg-slate-50 p-0.5"
        role="radiogroup"
        aria-label="Nível de destaque"
      >
        {ranks.map((rank) => {
          const selected = value === rank;
          return (
            <button
              key={rank}
              type="button"
              role="radio"
              aria-checked={selected}
              title={getRankTooltip(rank)}
              disabled={disabled}
              onClick={() => onChange(rank)}
              className={`h-8 min-w-0 flex-1 rounded px-1 text-[11px] font-semibold transition ${
                selected
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              {getRankShortLabel(rank)}
            </button>
          );
        })}
      </div>
      {value ? (
        <p className="mt-1 text-[11px] text-slate-500">{getRankLabel(value)}</p>
      ) : null}
      <FieldError message={error} />
    </div>
  );
}

export function YesNoToggle({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <SegmentedChoice
      value={value ? 1 : 0}
      onChange={(next) => onChange(next === 1)}
      options={[
        { value: 1, label: "Sim" },
        { value: 0, label: "Não" },
      ]}
    />
  );
}

export function CheckTile({
  checked,
  label,
  onChange,
}: {
  checked: boolean;
  label: string;
  onChange: (next: boolean) => void;
}) {
  return (
    <label
      className={`flex h-9 cursor-pointer items-center gap-2 rounded-md border px-2.5 text-[13px] font-medium ${
        checked
          ? "border-slate-300 bg-slate-50 text-slate-900"
          : "border-slate-200 bg-white text-slate-700"
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-3.5 w-3.5 shrink-0 accent-slate-800"
      />
      <span className="truncate">{label}</span>
    </label>
  );
}

export function GuaranteePills({
  items,
  isSelected,
  onToggle,
}: {
  items: Array<{ id: number; name: string }>;
  isSelected: (id: number) => boolean;
  onToggle: (id: number, selected: boolean) => void;
}) {
  if (items.length === 0) {
    return (
      <p className="text-[13px] text-slate-500">Nenhuma garantia cadastrada.</p>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => (
        <CheckTile
          key={item.id}
          label={item.name}
          checked={isSelected(item.id)}
          onChange={(next) => onToggle(item.id, next)}
        />
      ))}
    </div>
  );
}

export function PropertyStepActions({
  leading,
  trailing,
}: {
  leading?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <div className="sticky bottom-0 z-10 mt-8 flex min-w-0 flex-col gap-2 border-t border-slate-200 bg-white py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-wrap gap-2">{leading}</div>
      <div className="flex flex-wrap gap-2 sm:justify-end">{trailing}</div>
    </div>
  );
}

export function FormStack({ children }: { children: ReactNode }) {
  return <div className="flex w-full min-w-0 flex-col gap-8">{children}</div>;
}
